import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { MagnifyingGlass, Plus, ChatCircle, User, SignOut, List, Heart, Sparkle, ShoppingCart } from "@phosphor-icons/react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import NotificationBell from "@/components/NotificationBell";

export default function Navbar() {
  const { user, logout, cartCount } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#FDFBF7]/80 border-b border-[#EAE7E0]/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <span className="font-heading text-2xl sm:text-3xl font-semibold text-[#2C5545] tracking-tight">Borrow</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <Link to="/browse" className="text-sm font-medium text-[#5C665D] hover:text-[#2C5545] transition-colors" data-testid="nav-browse">
            Browse
          </Link>
          {user && (
            <>
              <Link to="/recommendations" className="text-sm font-medium text-[#5C665D] hover:text-[#2C5545] transition-colors flex items-center gap-1" data-testid="nav-recommendations">
                <Sparkle size={14} weight="duotone" /> For You
              </Link>
              <Link to="/create-listing" className="text-sm font-medium text-[#5C665D] hover:text-[#2C5545] transition-colors" data-testid="nav-create-listing">
                List an Item
              </Link>
            </>
          )}
        </nav>

        <div className="flex items-center gap-3">
          <Link to="/browse" className="md:hidden p-2 text-[#5C665D] hover:text-[#2C5545]" data-testid="nav-browse-mobile">
            <MagnifyingGlass size={20} weight="duotone" />
          </Link>

          {user ? (
            <>
              <NotificationBell />
              <Link to="/cart" className="p-2 text-[#5C665D] hover:text-[#2C5545] transition-colors relative" data-testid="nav-cart">
                <ShoppingCart size={20} weight="duotone" />
                {cartCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-[18px] h-[18px] bg-[#D96C4A] text-white rounded-full text-[9px] flex items-center justify-center font-semibold">{cartCount}</span>
                )}
              </Link>
              <Link to="/wishlist" className="p-2 text-[#5C665D] hover:text-[#D96C4A] transition-colors" data-testid="nav-wishlist">
                <Heart size={20} weight="duotone" />
              </Link>
              <Link to="/messages" className="p-2 text-[#5C665D] hover:text-[#2C5545] transition-colors relative" data-testid="nav-messages">
                <ChatCircle size={20} weight="duotone" />
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild data-testid="nav-user-menu-trigger">
                  <button className="flex items-center gap-2 rounded-full bg-[#2C5545] text-white px-3 py-1.5 text-sm font-medium hover:bg-[#224235] transition-all">
                    <User size={16} weight="duotone" />
                    <span className="hidden sm:inline">{user.name?.split(" ")[0]}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-white border-[#EAE7E0] rounded-xl">
                  <DropdownMenuItem onClick={() => navigate("/dashboard")} className="cursor-pointer" data-testid="nav-dashboard">
                    <List size={16} weight="duotone" className="mr-2" /> Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/recommendations")} className="cursor-pointer" data-testid="nav-recommendations-menu">
                    <Sparkle size={16} weight="duotone" className="mr-2" /> For You
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/wishlist")} className="cursor-pointer" data-testid="nav-wishlist-menu">
                    <Heart size={16} weight="duotone" className="mr-2" /> Wishlist
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/create-listing")} className="cursor-pointer md:hidden" data-testid="nav-create-listing-mobile">
                    <Plus size={16} weight="duotone" className="mr-2" /> List an Item
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-[#EAE7E0]" />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-[#D94A4A]" data-testid="nav-logout">
                    <SignOut size={16} weight="duotone" className="mr-2" /> Log Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <div className="flex items-center gap-2">
              <Link to="/login" className="text-sm font-medium text-[#5C665D] hover:text-[#2C5545] transition-colors px-3 py-1.5" data-testid="nav-login">
                Log In
              </Link>
              <Link to="/register" className="bg-[#2C5545] text-white hover:bg-[#224235] rounded-full px-4 py-1.5 text-sm font-medium transition-all hover:-translate-y-0.5 hover:shadow-md" data-testid="nav-register">
                Sign Up
              </Link>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
